/**
 * AdminLayout — Main admin shell
 * Vertical sidebar (desktop) + slide-over drawer (mobile)
 * Active nav state is derived from the current URL (no stale tab bugs)
 */

import {
  AccountCircle,
  Analytics,
  Assessment,
  GridView,
  TableView,
  Chat,
  ModeComment,
  Close as CloseIcon,
  Contacts,
  Diamond,
  Event,
  ExpandLess,
  ExpandMore,
  Folder,
  History,
  InsertDriveFile,
  PermMedia,
  KeyboardArrowDown,
  Language,
  Link as LinkIcon,
  Webhook as WebhookIcon,
  Lock,
  Logout,
  Menu as MenuIcon,
  NotificationsActive,
  Payments,
  People,
  PersonAdd,
  PlayArrow,
  Psychology,
  QuestionAnswer,
  ReceiptLong,
  Settings,
  SmartToy,
  WhatsApp,
  TextFields,
  Timeline,
  AccountTree,
  AccountBalanceWallet,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavTarget } from '@/utils/admin/lastListQuery';

import { useActivityTracker } from '@/hooks/useActivityTracker';
import { useAuth } from '@/hooks/useAuth';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { usePlanFeature } from '@/hooks/usePlanFeature';
import { useTerminology } from '@/hooks/useTerminology';
import {
  useWebSocket,
  type ConversationUnreadEvent,
  type ConversationRoutingEvent,
} from '@/hooks/useWebSocket';
import adminApi, { type ActivityBadges, type ActivitySection } from '@/services/admin.api';
import { ALERTS_CHANGED_EVENT, notifyAlertsChangedFromServer } from '@/utils/alerts-events.util';
import PlanLockDialog from '@/components/common/PlanLockDialog';
import PermissionLockDialog from '@/components/common/PermissionLockDialog';
import TenantBlockedDialog from '@/components/common/TenantBlockedDialog';
import { useHaltedWorkspaceRefresh } from '@/hooks/useHaltedWorkspaceRefresh';
import { roleLabel } from '@/utils/roleLabel.util';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/store';
import { showPermissionLock } from '@/store/slices/permissionLockSlice';
import { showTenantBlocked } from '@/store/slices/tenantBlockedSlice';
import { updateTenant } from '@/store/slices/authSlice';
import {
  TENANT_BLOCKED_EVENT,
  REACTIVATE_ROUTE,
  TenantBlockedPayload,
} from '@/utils/tenant-block-detection.util';
import { PERMISSION_DENIED_EVENT, PermissionLockPayload } from '@/utils/permission-lock-detection.util';
import { useWalletSummary } from '@/hooks/useWalletSummary';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from 'react-icons/tb';

// ─── Constants ───────────────────────────────────────────────────────────────
const TOKEN_REFRESH_INTERVAL = 12 * 60 * 1000; // refresh 3 min before the 15-min JWT expiry
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour of idle before auto-logout
const PLAN_REFRESH_INTERVAL = 5 * 60 * 1000; // poll /me for plan/entitlement changes every 5 min
const SIDEBAR_WIDTH = 240;
const SIDEBAR_MINI_WIDTH = 64;

// ─── Types ────────────────────────────────────────────────────────────────────
interface NavItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavItem[];
  badge?: number | undefined;
  /** Distinct from `badge` (unread messages) — count of conversations currently
   *  assigned to the logged-in user, rendered as a small pill after the label
   *  rather than overlapping the icon, so the two counts stay visually distinct. */
  secondaryBadge?: number | undefined;
  /** Plan doesn't include this feature — still navigable (clicking a gated CTA on the
   *  target page shows the PlanLockDialog), but rendered with a lock indicator here. */
  locked?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function isActivePath(path: string | undefined, currentPath: string): boolean {
  if (!path) return false;
  // Exact match or starts-with (for nested routes)
  return currentPath === path || currentPath.startsWith(path + '/');
}

function hasActiveChild(item: NavItem, currentPath: string): boolean {
  return item.children?.some((c) => isActivePath(c.path, currentPath)) ?? false;
}

// ─── NavLink ─────────────────────────────────────────────────────────────────
interface NavLinkProps {
  item: NavItem;
  depth?: number;
  collapsed?: boolean;
  open?: boolean;
  onToggle?: () => void;
  onNavigate?: ((path: string) => void) | undefined;
  currentPath: string;
}

const NavLink: React.FC<NavLinkProps> = ({
  item,
  depth = 0,
  collapsed = false,
  open = false,
  onToggle,
  onNavigate,
  currentPath,
}) => {
  const isActive = isActivePath(item.path, currentPath);
  const parentActive = hasActiveChild(item, currentPath);
  const hasChildren = Boolean(item.children?.length);
  const indentPx = depth * 12;

  return (
    <Box>
      <Tooltip title={collapsed && depth === 0 ? item.text : ''} placement="right" arrow>
        <Box
          role="button"
          tabIndex={0}
          aria-label={item.text}
          aria-expanded={hasChildren ? open : undefined}
          onClick={() => {
            if (hasChildren) {
              onToggle?.();
            } else if (item.path) {
              onNavigate?.(item.path);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (hasChildren) {
                onToggle?.();
              } else if (item.path) {
                onNavigate?.(item.path);
              }
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: collapsed ? 0 : `${14 + indentPx}px`,
            py: depth === 0 ? '9px' : '7px',
            mx: collapsed ? 0 : 1,
            borderRadius: 2,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.15s ease',
            justifyContent: collapsed ? 'center' : 'flex-start',
            position: 'relative',
            color: isActive || parentActive ? '#4f46e5' : '#475569',
            bgcolor: isActive
              ? alpha('#4f46e5', 0.1)
              : parentActive && depth === 0
                ? alpha('#4f46e5', 0.04)
                : 'transparent',
            '&:hover': {
              color: '#4f46e5',
              bgcolor: alpha('#4f46e5', isActive ? 0.1 : 0.06),
            },
            '&:focus-visible': { outline: '2px solid #4f46e5', outlineOffset: 2 },
          }}
        >
          {/* Active indicator bar */}
          {isActive && depth === 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: '20%',
                bottom: '20%',
                width: 3,
                bgcolor: '#4f46e5',
                borderRadius: '0 2px 2px 0',
              }}
            />
          )}

          {/* Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              opacity: item.locked ? 0.5 : 1,
              '& svg': { fontSize: depth === 0 ? '1.2rem' : '1rem' },
            }}
          >
            {item.badge ? (
              <Badge
                badgeContent={item.badge}
                color="error"
                max={99}
                overlap="circular"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.625rem', height: 16, minWidth: 16 } }}
              >
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </Box>

          {/* Label */}
          {!collapsed && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: isActive || parentActive ? 600 : 500,
                fontSize: depth === 0 ? '0.875rem' : '0.8125rem',
                flex: 1,
                lineHeight: 1,
                opacity: item.locked ? 0.5 : 1,
              }}
            >
              {item.text}
            </Typography>
          )}

          {!collapsed && !!item.secondaryBadge && (
            <Tooltip title="Assigned to you" placement="top">
              <Box
                sx={{
                  flexShrink: 0,
                  minWidth: 18,
                  height: 18,
                  px: 0.5,
                  borderRadius: '9px',
                  bgcolor: '#0f766e',
                  color: '#fff',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.secondaryBadge > 99 ? '99+' : item.secondaryBadge}
              </Box>
            </Tooltip>
          )}

          {/* Plan-locked indicator — still clickable, the target page shows its own upgrade overlay */}
          {!collapsed && item.locked && (
            <Tooltip title="Upgrade your plan to unlock" placement="top">
              <Lock sx={{ fontSize: '0.875rem', color: 'text.disabled', flexShrink: 0 }} />
            </Tooltip>
          )}

          {/* Chevron for parent items */}
          {!collapsed && hasChildren && (
            <Box sx={{ display: 'flex', color: 'text.disabled', '& svg': { fontSize: '1rem' } }}>
              {open ? <ExpandLess /> : <ExpandMore />}
            </Box>
          )}
        </Box>
      </Tooltip>

      {/* Children */}
      {hasChildren && !collapsed && (
        <Collapse in={open} timeout={200} unmountOnExit>
          <Box sx={{ mt: 0.25 }}>
            {item.children!.map((child) => (
              <NavLink
                key={child.text}
                item={child}
                depth={depth + 1}
                currentPath={currentPath}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ─── SidebarContent ───────────────────────────────────────────────────────────
interface SidebarContentProps {
  menuItems: NavItem[];
  collapsed: boolean;
  currentPath: string;
  onNavigate: (path: string) => void;
  onClose?: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  menuItems,
  collapsed,
  currentPath,
  onNavigate,
  onClose,
}) => {
  // Track which parent groups are open
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    menuItems.forEach((item) => {
      if (item.children?.some((c) => isActivePath(c.path, currentPath))) {
        initial.add(item.text);
      }
    });
    return initial;
  });

  // Auto-expand group that contains the active child when path changes
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.children?.some((c) => isActivePath(c.path, currentPath))) {
        setOpenGroups((prev) => new Set(prev).add(item.text));
      }
    });
  }, [currentPath, menuItems]);

  const toggleGroup = (text: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(text) ? next.delete(text) : next.add(text);
      return next;
    });

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
      {menuItems.map((item) => (
        <NavLink
          key={item.text}
          item={item}
          collapsed={collapsed}
          currentPath={currentPath}
          open={openGroups.has(item.text)}
          onToggle={() => toggleGroup(item.text)}
          onNavigate={(path) => {
            onNavigate(path);
            onClose?.();
          }}
        />
      ))}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, tenant, logout, refreshToken, refreshUser } = useAuth();
  const { hasRouteAccess, hasModuleAccess } = useModuleAccess();
  // Prepaid wallet: switched on per workspace by the platform (not by the plan).
  // One shared, background-safe GET — only for roles that can see billing.
  const { summary: wallet, walletEnabled } = useWalletSummary({ enabled: hasModuleAccess('billing.activePlan') });
  const { hasFeature } = usePlanFeature();
  const { terminology } = useTerminology();
  const dispatch = useDispatch<AppDispatch>();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [limitAlert, setLimitAlert] = useState<string | null>(null);
  const [unresolvedAlertCount, setUnresolvedAlertCount] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [activityBadges, setActivityBadges] = useState<ActivityBadges>({});
  const [assignedToMeCount, setAssignedToMeCount] = useState(0);
  // Drives the "Instagram Comments" nav item's visibility: it appears only when
  // the module is active (plan+RBAC) AND the account is actually connected.
  const [instagramConnected, setInstagramConnected] = useState(false);

  const lastTokenRefreshRef = useRef<number>(Date.now());
  // `user.tenantId` is set directly from the JWT on every non-superadmin login and is the
  // more fundamental source; `tenant.id` comes from a separate object that isn't always
  // repopulated (e.g. some refresh paths). Same fallback ChatLogs.tsx uses for its socket.
  const wsTenantId = (user as any)?.tenantId || tenant?.id;

  // ─── Global permission-denied dialog (auto-dispatch) ────────────────────
  // permission-lock-detection.util.ts's axios interceptor fires this window
  // event for any tagged, non-suppressed INSUFFICIENT_PERMISSIONS 403,
  // anywhere in the app — this is the one listener that turns it into the
  // actual Redux dispatch, since that util file deliberately stays store-free
  // (see its header comment). AdminLayout is mounted on every admin route
  // and already renders <PermissionLockDialog/>, so one listener here gives
  // every page this for free — no per-page wiring needed, unlike the
  // opt-in plan-lock pattern.
  useEffect(() => {
    const onPermissionDenied = (e: Event) => {
      const detail = (e as CustomEvent<PermissionLockPayload>).detail;
      if (detail) dispatch(showPermissionLock(detail));
    };
    window.addEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied);
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied);
  }, [dispatch]);

  // ─── Global 'workspace is read-only' dialog (auto-dispatch) ──────────────
  // tenant-block-detection.util.ts fires this window event whenever a write is
  // refused with TENANT_SUSPENDED / TENANT_INACTIVE (axios interceptor + raw-fetch
  // helper). Besides opening the dialog, sync the refused status into the stored
  // tenant so the banner below appears even if the session predates the change.
  useEffect(() => {
    const onTenantBlocked = (e: Event) => {
      const detail = (e as CustomEvent<TenantBlockedPayload>).detail;
      if (!detail) return;
      dispatch(showTenantBlocked(detail));
      if (tenant && tenant.status !== detail.status) {
        dispatch(updateTenant({ tenant: { ...tenant, status: detail.status } }));
      }
    };
    window.addEventListener(TENANT_BLOCKED_EVENT, onTenantBlocked);
    return () => window.removeEventListener(TENANT_BLOCKED_EVENT, onTenantBlocked);
  }, [dispatch, tenant]);

  // A halted workspace's status can change while this tab is open (the super-admin resumes it, or a
  // payment reactivates it) — keep the banner truthful. Does nothing at all for a healthy workspace.
  useHaltedWorkspaceRefresh(tenant?.status === 'suspended' || tenant?.status === 'inactive', refreshUser);

  // ─── Token / Activity tracking ──────────────────────────────────────────
  useActivityTracker({
    onActivity: async () => {
      if (!user) return;
      const now = Date.now();
      if (now - lastTokenRefreshRef.current >= TOKEN_REFRESH_INTERVAL) {
        try {
          const refreshed = await refreshToken();
          if (refreshed) {
            lastTokenRefreshRef.current = now;
          } else {
            logout();
          }
        } catch {
          logout();
        }
      }
    },
    onInactive: () => logout(),
    inactivityTimeout: INACTIVITY_TIMEOUT,
    activityCheckInterval: 60000,
  });

  useEffect(() => {
    if (user) {
      lastTokenRefreshRef.current = Date.now();
    }
  }, [user]);

  // ─── Plan/entitlement freshness ──────────────────────────────────────────
  // No websocket push for plan changes (upgrade/downgrade) — the backend gate is the
  // real boundary, a stale tab just can't see a feature it can't actually use anyway.
  // A 5-min poll + refetch-on-focus is enough to pick up server-side demotions without
  // hammering the API (same anti-polling stance as the billing-alerts effect above).
  const lastPlanRefreshRef = useRef<number>(Date.now());
  useEffect(() => {
    if (!user) return undefined;

    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastPlanRefreshRef.current >= PLAN_REFRESH_INTERVAL) {
        lastPlanRefreshRef.current = now;
        void refreshUser();
      }
    };

    const intervalId = setInterval(maybeRefresh, PLAN_REFRESH_INTERVAL);
    window.addEventListener('focus', maybeRefresh);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', maybeRefresh);
    };
  }, [user, refreshUser]);

  // ─── Billing alerts ──────────────────────────────────────────────────────
  // NOTE: load-once-on-mount only (no polling) to avoid hammering the admin API.
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const limits = await adminApi.getBillingUsageLimits({ suppressPermissionLock: true });
        if (!mounted) return;
        if (limits.message.exceeded) {
          setLimitAlert(
            'Message limit exceeded. Sending is blocked until you request more access.'
          );
          return;
        }
        if (limits.storage.exceeded) {
          setLimitAlert(
            'Storage limit is full. File uploads are blocked until you request more storage.'
          );
          return;
        }
        if (limits.apiCalls.exceeded) {
          setLimitAlert(
            'API call limit exceeded. API calls are blocked until you request more access.'
          );
          return;
        }
        setLimitAlert(null);
      } catch {
        /* keep layout stable */
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // ─── System alert badge ──────────────────────────────────────────────────
  // Counts open alerts nobody has acknowledged. Loaded on mount, then kept live
  // two ways: the `alerts:changed` socket event (new alert raised, or another
  // session acted) and the same-tab ALERTS_CHANGED_EVENT the System Alerts page
  // fires right after acknowledge/resolve, so the badge never lags the page.
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const summary = await adminApi.getAlertSummary({ suppressPermissionLock: true });
        if (mounted) setUnresolvedAlertCount(summary.unacknowledged ?? summary.total);
      } catch {
        /* keep last known count on transient failure */
      }
    };
    load();
    window.addEventListener(ALERTS_CHANGED_EVENT, load);
    return () => {
      mounted = false;
      window.removeEventListener(ALERTS_CHANGED_EVENT, load);
    };
  }, []);

  // ─── Chat Logs unread badge ──────────────────────────────────────────────
  // Load once on mount (matches the alerts badge above), then stay live via
  // the same tenant-room socket ChatLogs.tsx uses — a burst of inbound
  // messages/opens is debounced into a single cheap indexed-aggregate
  // refetch rather than patched in memory (no per-conversation state to
  // track here, so a refetch is both simpler and impossible to have drift).
  const unreadRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const summary = await adminApi.getConversationsUnreadSummary({ suppressPermissionLock: true });
        if (mounted) {
          setUnreadChatCount(summary.total);
          setAssignedToMeCount(summary.assignedToMe);
        }
      } catch {
        /* keep last known count on transient failure */
      }
    };
    load();
    return () => {
      mounted = false;
      if (unreadRefreshTimerRef.current) clearTimeout(unreadRefreshTimerRef.current);
    };
  }, []);

  // A burst of failing sends can raise many alerts at once — collapse them into one refetch.
  const alertsRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleAlertsRefresh = () => {
    if (alertsRefreshTimerRef.current) clearTimeout(alertsRefreshTimerRef.current);
    alertsRefreshTimerRef.current = setTimeout(() => notifyAlertsChangedFromServer(), 600);
  };

  // Debounced so an import burst of enquiries collapses into one badge refetch.
  const activityRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleActivityRefresh = () => {
    if (activityRefreshTimerRef.current) clearTimeout(activityRefreshTimerRef.current);
    activityRefreshTimerRef.current = setTimeout(() => loadActivityBadgesRef.current(), 800);
  };

  const scheduleUnreadRefresh = () => {
    if (unreadRefreshTimerRef.current) clearTimeout(unreadRefreshTimerRef.current);
    unreadRefreshTimerRef.current = setTimeout(() => {
      adminApi
        .getConversationsUnreadSummary({ suppressPermissionLock: true })
        .then((summary) => {
          setUnreadChatCount(summary.total);
          setAssignedToMeCount(summary.assignedToMe);
        })
        .catch(() => {
          /* keep last known count on transient failure */
        });
    }, 500);
  };

  // conversation:unread fires for every inbound increment/mark-as-read reset;
  // conversation:routing fires for every claim/assign/hand-back/auto-escalation
  // — both can change the assignedToMe count, so both refresh it.
  useWebSocket(wsTenantId, {
    onConversationUnread: (_: ConversationUnreadEvent) => scheduleUnreadRefresh(),
    onConversationRouting: (_: ConversationRoutingEvent) => scheduleUnreadRefresh(),
    onAlertsChanged: () => scheduleAlertsRefresh(),
    onActivityChanged: () => scheduleActivityRefresh(),
  });

  // ─── "New since last look" badges (Enquiries / Web Captures / IG Comments / Appointments) ──
  // Watermark-based (see activity-badges.controller): opening a section marks it
  // seen server-side and zeroes its badge; leaving it marks it seen again so rows
  // that arrived while you were reading it don't light up afterwards. Refreshed when you
  // enter/leave a section, on tab focus, and on the server's `activity:changed` socket hint
  // (debounced) — no polling, and no refetch on ordinary page-to-page navigation.
  const activitySectionForPath = (path: string): ActivitySection | null => {
    const prefix = path.startsWith('/admin') ? '/admin' : '';
    if (path.startsWith(`${prefix}/activity/leads`)) return 'enquiries';
    if (path.startsWith(`${prefix}/activity/web-captures`)) return 'webCaptures';
    if (path.startsWith(`${prefix}/activity/instagram-comments`)) return 'instagramComments';
    if (path === `${prefix}/appointments` || path.startsWith(`${prefix}/appointments/`)) return 'appointments';
    return null;
  };
  const loadActivityBadges = useCallback(() => {
    adminApi
      .getActivityBadges({ suppressPermissionLock: true })
      .then((b) => {
        const current = activitySectionForPathRef.current;
        setActivityBadges(current ? { ...b, [current]: 0 } : b);
      })
      .catch(() => {
        /* keep last known counts on transient failure */
      });
  }, []);
  const activitySectionForPathRef = useRef<ActivitySection | null>(null);
  const loadActivityBadgesRef = useRef(loadActivityBadges);
  loadActivityBadgesRef.current = loadActivityBadges;
  const activityBadgesInitRef = useRef(false);
  useEffect(() => {
    const prev = activitySectionForPathRef.current;
    const next = activitySectionForPath(location.pathname);
    activitySectionForPathRef.current = next;
    const firstRun = !activityBadgesInitRef.current;
    activityBadgesInitRef.current = true;
    const crossedSection = prev !== next;
    // Contacts → Dashboard etc. changes nothing the badges depend on: skip the request.
    if (!firstRun && !crossedSection) return;
    const marks: Array<Promise<unknown>> = [];
    if (prev && prev !== next) marks.push(adminApi.markActivitySeen(prev).catch(() => {}));
    if (next && next !== prev) {
      marks.push(adminApi.markActivitySeen(next).catch(() => {}));
      setActivityBadges((b) => ({ ...b, [next]: 0 }));
    }
    Promise.all(marks).then(() => loadActivityBadgesRef.current());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  useEffect(
    () => () => {
      if (activityRefreshTimerRef.current) clearTimeout(activityRefreshTimerRef.current);
      if (alertsRefreshTimerRef.current) clearTimeout(alertsRefreshTimerRef.current);
    },
    []
  );
  useEffect(() => {
    const onFocus = () => loadActivityBadgesRef.current();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // ─── Instagram connection status (gates the Comments nav item) ────────────
  // Only fetched when the module is even active for this tenant/role, so tenants
  // without Instagram never make this call. Determines whether the "Instagram
  // Comments" item shows at all — hidden unless connected.
  const instagramModuleActive = hasModuleAccess('instagram');
  useEffect(() => {
    if (!instagramModuleActive) {
      setInstagramConnected(false);
      return;
    }
    let mounted = true;
    adminApi
      // Background nav-gating prefetch — must degrade silently like the billing/
      // alerts/unread prefetches above. Without suppressPermissionLock a 403 here
      // (a role legitimately lacking Instagram access) auto-pops the global
      // "Access restricted" modal on dashboard load.
      .getInstagramConfig({ suppressPermissionLock: true })
      .then((cfg) => {
        if (mounted) setInstagramConnected(Boolean(cfg.isConnected));
      })
      .catch(() => {
        if (mounted) setInstagramConnected(false);
      });
    return () => {
      mounted = false;
    };
  }, [instagramModuleActive]);

  // ─── Build nav menu ───────────────────────────────────────────────────────
  const pathPrefix = location.pathname.startsWith('/admin') ? '/admin' : '';

  // Order: what a tenant DOES daily first (campaigns, automation, agents,
  // then the inbox/CRM), then knowledge + channels, then observability
  // (integrations, alerts, analytics, reports), then account admin.
  const allMenuItems: NavItem[] = [
    { text: 'Dashboard', icon: <GridView />, path: `${pathPrefix}/dashboard` },
    {
      text: 'Campaigns',
      icon: <Assessment />,
      path: `${pathPrefix}/campaigns`,
      locked: !hasFeature('campaignsEnabled'),
    },
    {
      text: 'Automation',
      icon: <AccountTree />,
      children: [
        { text: 'Workflows', icon: <AccountTree />, path: `${pathPrefix}/automation/workflows` },
        {
          text: 'API Connections',
          icon: <WebhookIcon />,
          path: `${pathPrefix}/automation/api-connections`,
          locked: !hasFeature('httpAutomationEnabled'),
        },
      ],
    },
    { text: 'Agents', icon: <SmartToy />, path: `${pathPrefix}/agents` },
    {
      // Renamed from "WhatsApp Templates" — the page it opens now has a
      // Templates | Interactive Messages tab strip (WhatsAppSectionTabs), so
      // one sidebar entry covers both. The path stays the Templates page;
      // the tab strip is how a user reaches Interactive Messages from there.
      text: 'WhatsApp',
      icon: <WhatsApp />,
      path: `${pathPrefix}/whatsapp-templates`,
      locked: !hasFeature('whatsappEnabled'),
    },
    { text: 'Playground', icon: <PlayArrow />, path: `${pathPrefix}/playground` },
    {
      text: 'Activity',
      icon: <Timeline />,
      children: [
        {
          text: 'Chat Logs',
          icon: <Chat />,
          path: `${pathPrefix}/activity/chat-logs`,
          badge: unreadChatCount > 0 ? unreadChatCount : undefined,
          secondaryBadge: assignedToMeCount > 0 ? assignedToMeCount : undefined,
        },
        {
          text: terminology.lead.plural,
          icon: <People />,
          path: `${pathPrefix}/activity/leads`,
          badge: activityBadges.enquiries || undefined,
        },
        { text: 'Inbound Media', icon: <PermMedia />, path: `${pathPrefix}/activity/media` },
        // Shown only when the Instagram module is active AND the account is
        // connected — see the extra instagramConnected gate in the menuItems
        // reduce below (hasRouteAccess alone only covers module plan+RBAC).
        {
          text: 'Instagram Comments',
          icon: <ModeComment />,
          path: `${pathPrefix}/activity/instagram-comments`,
          badge: activityBadges.instagramComments || undefined,
        },
        {
          text: 'Web Captures',
          icon: <Language />,
          path: `${pathPrefix}/activity/web-captures`,
          badge: activityBadges.webCaptures || undefined,
        },
      ],
    },
    { text: 'Contacts', icon: <Contacts />, path: `${pathPrefix}/contacts` },
    {
      text: 'Appointments',
      icon: <Event />,
      path: `${pathPrefix}/appointments`,
      badge: activityBadges.appointments || undefined,
    },
    {
      text: 'Sources',
      icon: <Folder />,
      children: [
        { text: 'Files', icon: <InsertDriveFile />, path: `${pathPrefix}/sources/files` },
        { text: 'Text', icon: <TextFields />, path: `${pathPrefix}/sources/text` },
        // Website source is hidden until it has real backend enforcement —
        // its route currently redirects to /sources/files and its module key
        // is hidden from the grant surface (same treatment as Analytics'
        // Topics/Sentiment stubs below).
        { text: 'Q&A', icon: <QuestionAnswer />, path: `${pathPrefix}/sources/qa` },
      ],
    },
    // Media is its own top-level destination ("Media Library") — the sendable
    // media files are a distinct concept from the knowledge Sources (Files /
    // Text / Q&A), so it reads as a peer, not a Sources child. URL is now
    // `${pathPrefix}/media` (old /sources/media redirects); module key stays
    // `sources.media`.
    { text: 'Media Library', icon: <PermMedia />, path: `${pathPrefix}/media` },
    { text: 'Web Chats', icon: <Language />, path: `${pathPrefix}/web-chats` },
    // Label stays "Connections" (as on main) — HubSpot/Google Sheets/etc. connectors.
    // Route + RBAC module key remain 'integrations'; Settings › "API Integration"
    // is the separate external-gateway config.
    { text: 'Connections', icon: <LinkIcon />, path: `${pathPrefix}/activity/integrations` },
    {
      text: 'System Alerts',
      icon: <NotificationsActive />,
      path: `${pathPrefix}/system-alerts`,
      badge: unresolvedAlertCount > 0 ? unresolvedAlertCount : undefined,
    },
    {
      text: 'Analytics',
      icon: <Analytics />,
      children: [
        { text: 'Chats', icon: <Assessment />, path: `${pathPrefix}/analytics/chats` },
        // Topics + Sentiment are hidden until their analytics are actually
        // built — the pages were "Coming soon" stubs with no backend, which
        // advertised features that don't exist. Page files + module keys are
        // kept for the future build (see ralplan-analytics-declutter.md).
      ],
    },
    {
      text: 'Reports',
      icon: <TableView />,
      path: `${pathPrefix}/reports`,
    },
    {
      text: 'Billing',
      icon: <ReceiptLong />,
      children: [
        { text: 'Active Plan', icon: <Diamond />, path: `${pathPrefix}/billing/active-plan` },
        { text: 'Billing Amount', icon: <Payments />, path: `${pathPrefix}/billing/amount` },
        { text: 'Billing AI', icon: <Psychology />, path: `${pathPrefix}/billing/ai` },
        { text: 'Wallet', icon: <AccountBalanceWallet />, path: `${pathPrefix}/billing/wallet` },
        { text: 'Usage History', icon: <History />, path: `${pathPrefix}/billing/usage-history` },
      ],
    },
    { text: 'User Management', icon: <PersonAdd />, path: `${pathPrefix}/user-management` },
    { text: 'Settings', icon: <Settings />, path: `${pathPrefix}/settings` },
  ];

  // Keyed off each item's `path` (via hasRouteAccess), not its `text` label
  // (the old hasMenuItemAccess(item.text) approach) — a tenant's vertical can
  // rename a label (e.g. terminology.lead.plural renders "Leads" as
  // "Enquiries" for some tenants), which broke the lookup in
  // menuItemToModuleMap and silently fell back to "allow" for any renamed or
  // unmapped label. Route paths are stable and already carried on every item.
  // The Instagram Comments item has a second gate beyond module plan+RBAC: the
  // account must be connected. Hidden entirely otherwise (a disconnected tenant
  // has nothing to show and can't act), matching the requirement that it appears
  // only when active as a module AND connected.
  const isNavItemVisible = (path?: string): boolean => {
    if (!path) return true;
    if (!hasRouteAccess(path)) return false;
    if (path.endsWith('/activity/instagram-comments')) return instagramConnected;
    if (path.endsWith('/billing/wallet')) return walletEnabled;
    return true;
  };

  const menuItems = allMenuItems.reduce<NavItem[]>((acc, item) => {
    if (item.children) {
      const filtered = item.children.filter((c) => isNavItemVisible(c.path));
      if (filtered.length > 0) acc.push({ ...item, children: filtered });
    } else if (isNavItemVisible(item.path)) {
      acc.push(item);
    }
    return acc;
  }, []);

  // ─── Sidebar drawer content ──────────────────────────────────────────────
  const sidebarInner = (forMobile = false) => (
    <Box
      sx={{
        width: forMobile ? SIDEBAR_WIDTH : collapsed ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          height: 58,
          px: collapsed && !forMobile ? 0 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed && !forMobile ? 'center' : 'space-between',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}
      >
        {/* Logo + wordmark — hidden while collapsed (desktop) so the rail
            shows only the sidebar toggle icon, not a logo/chevron pair. */}
        {(!collapsed || forMobile) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Baatly"
              sx={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', fontSize: '1rem' }}
            >
              Baatly
            </Typography>
          </Box>
        )}
        {/* Collapse toggle (desktop only) — same sidebar-rail icon whether
            expanded or collapsed, so the control reads as one persistent
            affordance rather than a generic chevron. */}
        {!forMobile && !isMobile && (
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton
              onClick={() => setCollapsed((p) => !p)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              sx={{
                color: '#0f172a',
                bgcolor: alpha('#0f172a', 0.06),
                borderRadius: 1.5,
                '&:hover': { bgcolor: alpha('#0f172a', 0.12) },
              }}
            >
              {collapsed ? (
                <TbLayoutSidebarLeftExpand size={22} />
              ) : (
                <TbLayoutSidebarLeftCollapse size={22} />
              )}
            </IconButton>
          </Tooltip>
        )}
        {/* Mobile close button */}
        {forMobile && (
          <IconButton size="small" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* Nav items */}
      <SidebarContent
        menuItems={menuItems}
        collapsed={collapsed && !forMobile}
        currentPath={location.pathname}
        onNavigate={(path) => navigate(resolveNavTarget(path, location.pathname))}
        onClose={() => setMobileOpen(false)}
      />

      {/* User section */}
      <Box
        sx={{
          borderTop: '1px solid #e2e8f0',
          p: collapsed && !forMobile ? 1 : 2,
          flexShrink: 0,
        }}
      >
        <Box
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          role="button"
          aria-label="User menu"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setUserMenuAnchor(e.currentTarget as any);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: collapsed && !forMobile ? 0 : 1.5,
            py: 1,
            borderRadius: 2,
            cursor: 'pointer',
            justifyContent: collapsed && !forMobile ? 'center' : 'flex-start',
            transition: 'all 0.15s ease',
            '&:hover': { bgcolor: alpha('#4f46e5', 0.06) },
            '&:focus-visible': { outline: '2px solid #4f46e5', outlineOffset: 2 },
          }}
        >
          <Avatar
            {...(user?.avatar ? { src: user.avatar } : {})}
            sx={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {(user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '')}
          </Avatar>
          {(!collapsed || forMobile) && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ color: 'text.primary', fontSize: '0.8125rem' }}
              >
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {roleLabel(user?.role)}
              </Typography>
            </Box>
          )}
          {(!collapsed || forMobile) && (
            <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
          )}
        </Box>
      </Box>
    </Box>
  );

  // ─── Top AppBar (mobile) ─────────────────────────────────────────────────
  const mobileAppBar = (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        display: { md: 'none' },
        bgcolor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        color: 'text.primary',
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: '56px !important', px: 2, gap: 1 }}>
        <IconButton
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
          edge="start"
          sx={{ color: 'text.secondary' }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Baatly"
            sx={{ width: 28, height: 28, objectFit: 'contain' }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.02em' }}
          >
            Baatly
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          size="small"
          aria-label="User menu"
        >
          <AccountCircle sx={{ color: 'text.secondary', fontSize: 26 }} />
        </IconButton>
      </Toolbar>
    </AppBar>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Mobile AppBar ─────────────────────────────────────────────────── */}
      {mobileAppBar}

      {/* ── Mobile Drawer ─────────────────────────────────────────────────── */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '4px 0 24px rgba(0,0,0,0.08)',
          },
        }}
      >
        {sidebarInner(true)}
      </Drawer>

      {/* ── Desktop Permanent Sidebar ──────────────────────────────────────── */}
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: collapsed ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH,
          flexShrink: 0,
          transition: 'width 0.2s ease',
        }}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: collapsed ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH,
            transition: 'width 0.2s ease',
            zIndex: theme.zIndex.drawer,
          }}
        >
          {sidebarInner(false)}
        </Box>
      </Box>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          pt: { xs: '56px', md: 0 },
        }}
      >
        {/* Read-only workspace banner — persistent, unlike the dialog (which only shows on a refused write). */}
        {(tenant?.status === 'suspended' || tenant?.status === 'inactive') && (
          <Alert
            severity={tenant.status === 'suspended' ? 'error' : 'warning'}
            sx={{ borderRadius: 0 }}
            action={
              tenant.status === 'inactive' ? (
                <Button color="inherit" size="small" onClick={() => navigate(REACTIVATE_ROUTE)}>
                  Reactivate
                </Button>
              ) : undefined
            }
          >
            <strong>Workspace {tenant.status}.</strong> You can view your data, but no changes can be made
            {tenant.status === 'suspended'
              ? '. Please contact support.'
              : ' until it is reactivated.'}
          </Alert>
        )}
        {/* Prepaid wallet low / empty — persistent until topped up (hidden on the wallet page itself). */}
        {walletEnabled &&
          wallet &&
          wallet.status === 'active' &&
          (wallet.lowBalance || wallet.available + wallet.creditLimit <= 0) &&
          !location.pathname.endsWith('/billing/wallet') && (
            <Alert
              severity={wallet.available + wallet.creditLimit <= 0 ? 'error' : 'warning'}
              sx={{ borderRadius: 0 }}
              action={
                <Button color="inherit" size="small" onClick={() => navigate(`${pathPrefix}/billing/wallet`)}>
                  Top up
                </Button>
              }
            >
              {wallet.available + wallet.creditLimit <= 0 ? (
                <>
                  <strong>Wallet balance used up.</strong> Usage paid from your wallet is paused until you top up.
                </>
              ) : (
                <>
                  <strong>Wallet balance is low</strong> ({wallet.available.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}).
                  Top up to avoid interruptions.
                </>
              )}
            </Alert>
          )}
        <Box
          className="page-content"
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3, lg: 4 },
            maxWidth: 1600,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* ── User Menu ─────────────────────────────────────────────────────── */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transitionDuration={150}
        PaperProps={{
          elevation: 3,
          sx: { mt: -1, minWidth: 200, borderRadius: 2.5, border: '1px solid #e2e8f0' },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {roleLabel(user?.role)}
          </Typography>
        </Box>

        <MuiMenuItem
          onClick={() => {
            navigate(`${pathPrefix}/account`);
            setUserMenuAnchor(null);
          }}
          sx={{ py: 1.25, fontSize: '0.875rem', gap: 1.5, '&:hover': { color: 'primary.main' } }}
        >
          <AccountCircle sx={{ fontSize: 18, color: 'text.secondary' }} />
          My Account
        </MuiMenuItem>

        <MuiMenuItem
          onClick={() => {
            navigate(`${pathPrefix}/settings`);
            setUserMenuAnchor(null);
          }}
          sx={{ py: 1.25, fontSize: '0.875rem', gap: 1.5, '&:hover': { color: 'primary.main' } }}
        >
          <Settings sx={{ fontSize: 18, color: 'text.secondary' }} />
          Settings
        </MuiMenuItem>

        <Divider />

        <MuiMenuItem
          onClick={logout}
          sx={{
            py: 1.25,
            fontSize: '0.875rem',
            gap: 1.5,
            color: 'error.main',
            '&:hover': { bgcolor: alpha('#ef4444', 0.06) },
          }}
        >
          <Logout sx={{ fontSize: 18 }} />
          Sign out
        </MuiMenuItem>
      </Menu>

      {/* ── Limit Alert ───────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(limitAlert)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={8000}
        onClose={() => setLimitAlert(null)}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setLimitAlert(null)}
          sx={{ borderRadius: 2.5, boxShadow: '0 8px 32px rgba(0,0,0,.15)' }}
        >
          {limitAlert}
        </Alert>
      </Snackbar>

      {/* ── Plan/quota restriction lock dialog ───────────────────────────── */}
      <PlanLockDialog />
      {/* ── RBAC permission restriction lock dialog ──────────────────────── */}
      <PermissionLockDialog />
      {/* ── Read-only workspace (suspended / inactive) dialog ────────────── */}
      <TenantBlockedDialog />
    </Box>
  );
};

export default AdminLayout;
